/**
 * JuicyAds script loader.
 *
 * Their published snippet is a `<script src=…jads.js>` plus a push onto a global
 * `adsbyjuicy` queue, one push per zone. That shape assumes a server-rendered page
 * where the tag sits next to its `<ins>`; in a SPA the script must load exactly once
 * across every slot and every route change, so it lives here behind a cached promise
 * rather than in the component.
 *
 * Nothing here is called until a slot has decided it is allowed to render — see
 * AdSlot. In particular the script is never fetched in safe mode or before the age
 * gate passes, so a suppressed slot makes no request to the network at all.
 */

const SCRIPT_SRC = "https://poweredby.jads.co/js/jads.js";

declare global {
  interface Window {
    adsbyjuicy?: Array<{ adzone: number }>;
  }
}

let pending: Promise<void> | null = null;
/** Set once jads.js has run its boot, which is the one time it reads the queue. */
let booted = false;
const AD_ORIGIN = "https://poweredby.jads.co";

/**
 * The queue jads.js drains, created on demand.
 *
 * ORDER MATTERS, and not in the way the API suggests. jads.js dereferences the global
 * the moment it executes, with no guard:
 *
 *     a = window.adsbyjuicy;
 *     if (!a || !a.loaded) { …; a.adsbyjuicy = { loaded: true }; … }
 *
 * If the queue does not exist yet, that second line throws on `undefined` and the
 * script dies before requesting a single ad — silently, since the throw is inside
 * their code. JuicyAds' published snippet never hits this because its inline push runs
 * while the async <script> is still downloading, so the array always exists first.
 *
 * Loading the script and pushing in its onload therefore looks correct and never
 * fills. Seed the queue before the tag is appended, and push before loading.
 *
 * THE QUEUE IS READ ONCE (measured 2026-10-21). Boot walks the array a single time,
 * creating each zone's iframe on the spot, and never looks at it again: a zone pushed
 * after jads.js has run gets no adshow request at all. Browse's in-feed slot mounts
 * only once /api/catalog answers, which is usually after the sidebar and footer slots
 * have already loaded the script — so that zone (1124375) is never requested and its
 * slot ends on the house card at the deadline, at 375px and at 1280px alike. The zone
 * itself serves at 375px when it is queued before boot. Measured on prod over the 14 days
 * to 2026-11-03: 73 fills in 1,419 in-feed renders, against 44% for the footer.
 *
 * FIXED BY placeLateZone below (2026-11-03): a zone pushed after boot gets the same
 * iframe boot would have built for it.
 */
function queue(): Array<{ adzone: number }> {
  return (window.adsbyjuicy = window.adsbyjuicy ?? []);
}

/** Load jads.js once per page. Repeat calls share the first call's promise. */
export function loadJuicyAds(): Promise<void> {
  if (pending) return pending;
  queue();
  pending = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      resolve();
      return;
    }
    const el = document.createElement("script");
    el.src = SCRIPT_SRC;
    el.async = true;
    // Part of JuicyAds' own snippet: without it a Cloudflare Rocket Loader-style
    // rewriter can defer the script past the pushes we have already queued.
    el.setAttribute("data-cfasync", "false");
    // jads.js boots synchronously as it executes, which is before load fires.
    el.addEventListener("load", () => {
      booted = true;
      resolve();
    });
    el.addEventListener("error", () => {
      // Overwhelmingly this is an ad blocker rather than a network fault. Clear the
      // cached promise so a later route change can retry, and let the caller collapse
      // its reserved box instead of leaving a permanent hole in the layout.
      pending = null;
      // The dead tag goes too: left in <head>, the `existing` check above would take it
      // for a loaded script on that retry and resolve at once, and the slot would wait
      // out its whole deadline before showing the house card instead of at once.
      el.remove();
      reject(new Error("JuicyAds script blocked or unavailable"));
    });
    document.head.appendChild(el);
  });
  return pending;
}

/**
 * Queue one zone for fill. Call this BEFORE loadJuicyAds — see the note on queue().
 * Returns true when the script had already booted, so the queue will never be read again
 * and the caller must place the zone itself with placeLateZone.
 */
export function pushZone(zoneId: string): boolean {
  const adzone = Number(zoneId);
  if (!Number.isFinite(adzone)) return false;
  queue().push({ adzone });
  return booted;
}

/**
 * Put a zone's ad in its slot the way jads.js boot does, for a zone that arrived after
 * boot. Boot's own code (jads2.js, read 2026-11-03) finds `<ins id={zone}>` and builds an
 * iframe on `poweredby.jads.co/adshow.php?adzone={zone}` sized from the ins's data-width and
 * data-height, frameborder 0, no scrolling, transparent. This builds that same iframe in
 * the same place. It skips boot's hover z-index juggling, which `isolate` on the slot
 * makes unnecessary anyway (see AdSlot).
 */
export function placeLateZone(host: HTMLElement, zoneId: string): boolean {
  if (!/^[0-9]{1,16}$/.test(zoneId)) return false;
  const ins = host.querySelector<HTMLElement>(`ins[id="${zoneId}"]`);
  if (!ins || host.querySelector("iframe")) return false;
  const frame = document.createElement("iframe");
  frame.src = `${AD_ORIGIN}/adshow.php?adzone=${zoneId}`;
  frame.width = ins.getAttribute("data-width") ?? "";
  frame.height = ins.getAttribute("data-height") ?? "";
  frame.setAttribute("frameborder", "0");
  frame.setAttribute("marginheight", "0");
  frame.setAttribute("marginwidth", "0");
  frame.setAttribute("scrolling", "no");
  frame.setAttribute("allowtransparency", "true");
  frame.style.border = "0";
  ins.replaceWith(frame);
  return true;
}

/**
 * Does this $exception come from a script the browser refuses to place?
 *
 * Lives here rather than in analytics.ts because jads.js is what produces these: it is
 * the only script this app loads without `crossorigin`, and it cannot have it —
 * poweredby.jads.co sends no Access-Control-Allow-Origin (verified 2026-08-27), so a
 * CORS-mode request would be refused outright and take the ads with it. The creatives it
 * injects afterwards are yet other origins on top. The same throwing-inside-their-own-code
 * behaviour documented on queue() above is what lands in the error tracker, and neither
 * the stack nor the fix is ours to reach.
 *
 * Browsers mask it in two shapes, and this matches both:
 *
 *  1. NO FRAMES AT ALL. `window.onerror` gets the bare string "Script error." and nothing
 *     else. Safari sometimes lets the real message through while still withholding every
 *     frame ("Can't find variable: EmptyRanges", 53 events in the 30 days to 2026-09-09,
 *     every one frameless), so matching the message text catches half of it and misses
 *     whatever the next ad-script variable is called.
 *  2. FRAMES WEBKIT ITSELF HID. Safari reports the frame but replaces its filename with
 *     the literal scheme `webkit-masked-url://hidden/` — its own "cross-origin, not
 *     telling you" marker ("null is not an object (evaluating 's.id')", same window).
 *
 * The rule is therefore structural, not a message blocklist: an exception no frame of
 * which names a real source is not ours to fix. Our own bundle ships sourcemaps at
 * deploy, so a first-party error always carries at least one frame with a real filename —
 * of the 18 distinct exception messages in that window, only the two above matched.
 */
export function isMaskedCrossOriginException(properties: Record<string, unknown> | undefined): boolean {
  const list = properties?.["$exception_list"];
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.every((entry: { stacktrace?: { frames?: Array<{ filename?: unknown }> } }) => {
    const frames = entry?.stacktrace?.frames;
    if (!Array.isArray(frames) || frames.length === 0) return true;
    return frames.every((f) => String(f?.filename ?? "").startsWith("webkit-masked-url:"));
  });
}
